// Document service for fetching user files from S3

// Set to true for local testing without AWS
const USE_MOCK_DATA = false;

export interface Document {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
  type: string;
  url?: string;
}

// Mock documents for testing
const mockDocuments: { [userId: string]: Document[] } = {
  // Admin users
  'admin-001': [
    {
      key: 'admin-001/documents/system-config.pdf',
      name: 'system-config.pdf',
      size: 156000,
      lastModified: new Date('2024-01-24'),
      type: 'application/pdf',
    },
    {
      key: 'admin-001/documents/user-audit-log.xlsx',
      name: 'user-audit-log.xlsx',
      size: 890000,
      lastModified: new Date('2024-01-25'),
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    {
      key: 'admin-001/documents/admin-guide.pdf',
      name: 'admin-guide.pdf',
      size: 2400000,
      lastModified: new Date('2024-01-20'),
      type: 'application/pdf',
    },
  ],
  'admin-002': [
    {
      key: 'admin-002/documents/backup-procedures.docx',
      name: 'backup-procedures.docx',
      size: 78000,
      lastModified: new Date('2024-01-22'),
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    {
      key: 'admin-002/documents/security-policy.pdf',
      name: 'security-policy.pdf',
      size: 345000,
      lastModified: new Date('2024-01-23'),
      type: 'application/pdf',
    },
  ],
  // Regular users
  'user-001': [
    {
      key: 'user-001/documents/contract.pdf',
      name: 'contract.pdf',
      size: 245000,
      lastModified: new Date('2024-01-15'),
      type: 'application/pdf',
    },
    {
      key: 'user-001/documents/report-2024.pdf',
      name: 'report-2024.pdf',
      size: 512000,
      lastModified: new Date('2024-01-20'),
      type: 'application/pdf',
    },
    {
      key: 'user-001/documents/invoice.docx',
      name: 'invoice.docx',
      size: 89000,
      lastModified: new Date('2024-01-22'),
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
  ],
  'user-002': [
    {
      key: 'user-002/documents/presentation.pdf',
      name: 'presentation.pdf',
      size: 1024000,
      lastModified: new Date('2024-01-18'),
      type: 'application/pdf',
    },
    {
      key: 'user-002/documents/notes.docx',
      name: 'notes.docx',
      size: 45000,
      lastModified: new Date('2024-01-21'),
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
  ],
  'user-003': [
    {
      key: 'user-003/documents/project-plan.pdf',
      name: 'project-plan.pdf',
      size: 678000,
      lastModified: new Date('2024-01-19'),
      type: 'application/pdf',
    },
    {
      key: 'user-003/documents/meeting-notes.docx',
      name: 'meeting-notes.docx',
      size: 34000,
      lastModified: new Date('2024-01-21'),
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    {
      key: 'user-003/documents/budget.xlsx',
      name: 'budget.xlsx',
      size: 156000,
      lastModified: new Date('2024-01-23'),
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    {
      key: 'user-003/documents/requirements.pdf',
      name: 'requirements.pdf',
      size: 423000,
      lastModified: new Date('2024-01-24'),
      type: 'application/pdf',
    },
  ],
};

export type DocumentCategory = 'medical' | 'pharmacy' | 'personal' | 'health-summary';

export const listUserDocuments = async (userId: string, targetUsername?: string, category?: DocumentCategory): Promise<Document[]> => {
  if (USE_MOCK_DATA) {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    return mockDocuments[userId] || [];
  } else {
    // Real S3 implementation - using private path for user-specific files
    const { list } = await import('aws-amplify/storage');

    let basePath: string;
    if (targetUsername) {
      // Admin viewing another user's files by username
      basePath = `users/${targetUsername}`;
    } else {
      // User viewing their own files - get username from auth
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const username = session.tokens?.idToken?.payload?.['cognito:username'] as string;
      basePath = username ? `users/${username}` : '';
    }

    if (!basePath) {
      return [];
    }

    // Add category to path if specified
    const searchPath = category ? `${basePath}/${category}/` : `${basePath}/`;
    console.log('Listing documents from path:', searchPath);
    const result = await list({ path: searchPath });
    console.log('Found items:', result.items);

    return result.items
      .filter(item => {
        const fileName = item.path.split('/').pop() || '';
        return fileName && !item.path.endsWith('/');
      })
      .map((item) => ({
        key: item.path,
        name: item.path.split('/').pop() || '',
        size: item.size || 0,
        lastModified: item.lastModified || new Date(),
        type: getFileType(item.path),
      }));
  }
};

// List all users who have documents (for admin)
export const listAllUserDocuments = async (): Promise<{ userId: string; documents: Document[] }[]> => {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return Object.entries(mockDocuments).map(([userId, documents]) => ({
      userId,
      documents,
    }));
  } else {
    // For real S3, list all users/* folders
    const { list } = await import('aws-amplify/storage');
    const result = await list({ path: 'users/' });

    // Group documents by username
    const userDocs: { [key: string]: Document[] } = {};
    result.items.forEach((item) => {
      const parts = item.path.split('/');
      if (parts.length >= 3) {
        const username = parts[1];
        if (!userDocs[username]) {
          userDocs[username] = [];
        }
        // Only add actual files, not folder markers
        const fileName = item.path.split('/').pop() || '';
        if (fileName) {
          userDocs[username].push({
            key: item.path,
            name: fileName,
            size: item.size || 0,
            lastModified: item.lastModified || new Date(),
            type: getFileType(item.path),
          });
        }
      }
    });

    return Object.entries(userDocs).map(([userId, documents]) => ({
      userId,
      documents,
    }));
  }
};

export const getDocumentUrl = async (key: string): Promise<string> => {
  if (USE_MOCK_DATA) {
    // Return a mock URL for testing
    return `https://mock-s3-bucket.s3.ca-central-1.amazonaws.com/${key}`;
  } else {
    // Real S3 implementation
    const { getUrl } = await import('aws-amplify/storage');
    const result = await getUrl({
      path: key,
      options: {
        expiresIn: 3600, // URL expires in 1 hour
      },
    });
    return result.url.toString();
  }
};

export const uploadDocument = async (
  userId: string,
  file: File,
  targetUsername?: string,
  category?: DocumentCategory
): Promise<Document> => {
  if (USE_MOCK_DATA) {
    const key = `${userId}/documents/${file.name}`;
    // Simulate upload
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const newDoc: Document = {
      key,
      name: file.name,
      size: file.size,
      lastModified: new Date(),
      type: file.type,
    };
    // Add to mock data
    if (!mockDocuments[userId]) {
      mockDocuments[userId] = [];
    }
    mockDocuments[userId].push(newDoc);
    return newDoc;
  } else {
    // Real S3 implementation - upload to user folder by username
    const { uploadData } = await import('aws-amplify/storage');

    let basePath: string;
    if (targetUsername) {
      // Admin uploading to another user's folder
      basePath = `users/${targetUsername}`;
    } else {
      // User uploading to their own folder - get username from auth
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const username = session.tokens?.idToken?.payload?.['cognito:username'] as string;
      basePath = `users/${username}`;
    }

    // Add category to path if specified
    const uploadPath = category
      ? `${basePath}/${category}/${file.name}`
      : `${basePath}/${file.name}`;

    console.log('Uploading to path:', uploadPath);

    const result = await uploadData({
      path: uploadPath,
      data: file,
      options: {
        contentType: file.type,
      },
    }).result;

    return {
      key: result.path,
      name: file.name,
      size: file.size,
      lastModified: new Date(),
      type: file.type,
    };
  }
};

export const deleteDocument = async (key: string): Promise<void> => {
  if (USE_MOCK_DATA) {
    // Simulate delete
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Remove from mock data
    Object.keys(mockDocuments).forEach((userId) => {
      mockDocuments[userId] = mockDocuments[userId].filter(
        (doc) => doc.key !== key
      );
    });
  } else {
    // Real S3 implementation
    const { remove } = await import('aws-amplify/storage');
    await remove({ path: key });
  }
};

const getFileType = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase();
  const types: { [key: string]: string } = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
  };
  return types[ext || ''] || 'application/octet-stream';
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
